<script setup lang="ts">
import BurgerIcon from '@/shared/ui/Icons/Burger.vue';
import Menu from '@/widgets/menu/index';
import LangButton from '@/widgets/header/ui/LangButton.vue';
import { COMPANY_TELEGRAM_LINK } from '@/shared/constants/contacts';

const { t } = useI18n();
const localePath = useLocalePath();

const isMenuOpen = ref(false);

const closeMenu = () => {
  isMenuOpen.value = false;
};

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};
</script>

<template>
  <div class="top-header">
    <div class="top-header__item">
      <span class="top-header__text _info">
        {{ t('headerInfo') }}
      </span>
    </div>
    <div class="top-header__item _nav">
      <ul class="top-header__list">
        <li class="top-header__list-item">
          <nuxt-link
            class="top-header__link"
            :to="localePath('for-business')"
            :aria-label="t('becomeOurPartner')"
          >
            {{ t('becomeOurPartner') }}
          </nuxt-link>
        </li>
        <li class="top-header__list-item">
          <nuxt-link
            class="top-header__link"
            :to="localePath('faq')"
            :aria-label="t('questionAnswer')"
          >
            {{ t('questionAnswer') }}
          </nuxt-link>
        </li>
      </ul>
      <div class="top-header__social">
        <a
          class="top-header__link"
          :href="COMPANY_TELEGRAM_LINK"
          target="_blank"
          :aria-label="t('supportTelegram')"
        >
          {{ t('supportTelegram') }}
        </a>
      </div>
      <div class="top-header__buttons">
        <ClientOnly>
          <LangButton />
        </ClientOnly>
        <ClientOnly>
          <button
            v-click-outside="closeMenu"
            class="top-header__button _menu"
            :aria-label="t('menu')"
            @click.stop="toggleMenu"
          >
            <BurgerIcon class="top-header__icon" />
            {{ t('menu') }}
            <transition name="fade" mode="out-in">
              <div v-if="isMenuOpen" class="top-header__menu-wrapper">
                <Menu />
              </div>
            </transition>
          </button>
        </ClientOnly>
      </div>
    </div>
  </div>
</template>

<style lang="less">
.top-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: @size-x6;

  &__text {
    display: block;
    font-size: 15px;
    line-height: 100%;
    font-weight: 400;
    color: @color-blue-bayoux;

    @media @tablet {
      font-size: 15px;

      &._info {
        max-width: 200px;
      }
    }
  }

  &__link {
    font-size: 15px;
    line-height: 100%;
    font-weight: 400;
    color: @color-oxford-blue;
    white-space: nowrap;
    transition: color @A linear;

    &:hover {
      color: @color-tango;
    }

    @media @tablet {
      font-size: 15px;
    }
  }

  &__item {
    &._nav {
      display: inline-flex;
    }
  }

  &__list {
    display: inline-flex;
    align-items: center;
    gap: 26px;
    margin-right: 36px;
  }

  &__social {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-right: 36px;
  }

  &__buttons {
    display: inline-flex;
    gap: 26px;
  }

  &__button {
    .button-reset();

    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 15px;
    line-height: 100%;
    font-weight: 400;
    color: @color-oxford-blue;
    transition:
      background-color @A linear,
      border @A linear;

    &._menu {
      padding: 9px @size-x3;
      border: 1px solid @color-athens-gray;
      border-radius: @size-x4;
      font-weight: 500;
      position: relative;

      &:hover {
        background-color: @color-athens-gray-dark;
        border: 1px solid @color-athens-gray-dark;
      }
    }

    @media @tablet {
      font-size: 15px;
    }
  }

  &__menu-wrapper {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    min-width: 100%;
    background-color: white;
    padding: @size-x4;
    width: 259px;
    z-index: 10;
    box-shadow: 0px 2px 7px 0px rgba(0, 0, 0, 0.27);
    border-radius: @size-x3;
  }

  &__icon {
    width: @size-x3;
    height: @size-x3;
  }
}
</style>
